import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { CreatePickupApi, ListCommanWay, GetCommanWay, UpdateCommanWay, CreateCommanWay } from "API/commonApi";
import { AxiosResponse } from "axios";
import Modal from "components/Modal/Modal";
import { useFormik } from "formik";
import { debounce } from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import UseToast from "utils/AfToast";
import { roles } from "utils/constant";
import { AfDataType as ADT } from "utils/TableColumnData";
import { number, object, string } from "yup";
import { MemoizedPickupDatatable } from "./PickupDatatable";
import Spinner from "utils/Spinner";
import { DocumentDuplicateIcon, EnvelopeOpenIcon } from "@heroicons/react/24/outline";
import ViewPickupMail from "pages/Report/ViewPickupMail";
import { Button } from "@heroui/react";

// Modern compact select styles
const pickupSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "0px 2px",
    minHeight: "28px",
    fontSize: "12px",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#0077B6" },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 6px",
  }),
  input: (base: any) => ({
    ...base,
    margin: "0px",
    padding: "0px",
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: "28px",
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b",
    fontSize: "12px",
    padding: "4px 10px",
    cursor: "pointer",
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
    zIndex: 9999,
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#94a3b8",
    fontSize: "12px",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#1e293b",
    fontSize: "12px",
  }),
};

const MInput = ({ label, name, type = "text", disabled = false, onBlurExtra, className = "", value, onChange, onBlur }: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <label className="text-[11px] font-medium text-slate-800 whitespace-nowrap min-w-[52px] text-right">{label}</label>
    <input
      type={type}
      name={name}
      value={value ?? ""}
      onChange={onChange}
      onBlur={(e) => {
        onBlur?.(e);
        onBlurExtra?.(e);
      }}
      disabled={disabled}
      placeholder={label}
      className={`flex-1 min-w-0 px-2 py-[3px] rounded-lg border text-[13px] transition-all duration-200 outline-none
        ${
          disabled
            ? "bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
            : "bg-white border-slate-200 hover:border-primary2 focus:border-primary2 focus:ring-2 focus:ring-primary2/20"
        } text-slate-700 placeholder:text-slate-400`}
    />
  </div>
);

const MSelect = ({ label, name, options, disabled = false, className = "", value, onChange, onBlur }: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <label className="text-[11px] font-medium text-slate-500 whitespace-nowrap min-w-[52px] text-right">{label}</label>
    <Select
      options={options}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      isDisabled={disabled}
      styles={pickupSelectStyles}
      placeholder={label}
      className="flex-1 min-w-0"
      menuPortalTarget={document.body}
    />
  </div>
);

const MCheckbox = ({ label, name, value, onChange, className = "" }: any) => (
  <label
    className={`inline-flex items-center gap-1 px-2 py-2 rounded-lg border border-slate-200 bg-white hover:border-primary2 transition-all cursor-pointer text-[14px] font-medium text-slate-600 ${className}`}
  >
    <input
      type="checkbox"
      name={name}
      checked={value != "0" && !!value}
      onChange={onChange}
      className="w-5 h-5 rounded border-slate-300 text-primary2 focus:ring-primary2/20"
    />
    {label}
  </label>
);

const getPkgBoxSizeValue = (value: any): string => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return "";
    }

    if (!["{", "["].includes(trimmedValue[0])) {
      return trimmedValue;
    }

    try {
      return getPkgBoxSizeValue(JSON.parse(trimmedValue));
    } catch {
      const match = trimmedValue.match(/boxSize\s*[:=]\s*["']?([^"'}]+)["']?/i);
      return match?.[1]?.trim() || "";
    }
  }

  if (typeof value === "object") {
    if ("boxSize" in value) {
      return getPkgBoxSizeValue(value.boxSize);
    }
  }

  return "";
};

const ToPayPickpAdd: React.FC = () => {
  let isDisabled = false;
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [typeOpt, setTypeOtp] = useState<any>();
  const [ten_30Opt, setTen_30Opt] = useState<any>();
  const [holidayOpt, setHolidayOtp] = useState<any>();
  const [branchOpt, setBranchOtp] = useState<any>();
  const [subbranchOpt, setSubBranchOtp] = useState<any>();
  const [productOpt, setProductOtp] = useState<any>();
  const [agentOpt, setAgentOtp] = useState<any>();
  const [isBranchSet, setIsBranchSet] = useState<any>();
  const [pincodeSection, setPincodeSection] = useState<any>("");
  const [mobileSection, setMobileSection] = useState<any>("");
  const [pickupCode, setPickupCode] = useState("");
  const [addedPickupDate, setAddedPickupDate] = useState("");
  const [isDataAdded, setIsDataAdded] = useState<any>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [manualEmailPopup, setManualEmailPopup] = useState(false);
  const [boxSizePopup, setBoxSizePopup] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedData, setSelectedData] = useState<any>({});
  const [filters, setFilters] = useState<any>({
    filter_date: moment().format("YYYY-MM-DD"),
    filter_date_to: moment().format("YYYY-MM-DD"),
  });
  const [userDetails, setUserDetails] = useState<any>({});

  // Memoize the datatable query — only recompute when isDataAdded toggles (Search click)
  const datatableQuery = useMemo(() => {
    return filters?.filter_pickup_code || filters?.filter_date || filters?.filter_date_to
      ? {
          ...(filters.filter_pickup_code && {
            pickup_code: filters.filter_pickup_code,
          }),
          ...(!filters.filter_pickup_code &&
            filters.filter_date && {
              pickup_date_time: {
                $gte: filters.filter_date,
                $lte: moment(filters?.filter_date_to).endOf("day"),
              },
              filter_no_booking: filters.filter_no_booking,
            }),
          ...(filters.filter_customer_code && {
            customer_code: filters.filter_customer_code,
          }),
          ...(filters.filter_origin && {
            shipper_city: filters.filter_origin,
          }),
          ...(filters.filter_destination && {
            consignee_city: filters.filter_destination,
          }),
        }
      : {
          filter_no_booking: filters.filter_no_booking,
          ...(filters.filter_customer_code && {
            customer_code: filters.filter_customer_code,
          }),
          ...(filters.filter_origin && {
            shipper_city: filters.filter_origin,
          }),
          ...(filters.filter_destination && {
            consignee_city: filters.filter_destination,
          }),
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataAdded]);

  const [old_pickup_code, setold_pickup_code] = useState<any>("");

  const [statusReason, setStatusReason] = useState<any>();
  const [selectedStatus, setSelectedStatus] = useState<any>({ value: -1, label: "" });
  const [rescheduleDate, setRescheduleDate] = useState<any>();

  const [employeeWapp, setEmployeeWapp] = useState<any>("");
  const [custCodeValid, setCustCodeValid] = useState<boolean | null>(null); // null = not checked, true = valid, false = invalid

  const [pickupDate, setPickupDate] = useState<any>(moment().format("YYYY-MM-DD"));
  const [pickupTime, setPickupTime] = useState<any>(moment().format("HH:mm"));

  const tableColumnData = useMemo(
    () => [
      {
        label: "Sr. ",
        key: "sr_no",
        type: ADT.TEXT,
        view: true,
        add: false,
        edit: false,
        filter: false,
        sort: false,
      },
      {
        label: "Action",
        key: "id",
        render: (_) => {
          return `<a>${_}</a>`;
        },
        type: ADT.EDIT,
        view: true,
        add: false,
        edit: false,
        filter: false,
        sort: false,
      },
      {
        label: "Cust Code",
        key: "customer_code",
        type: ADT.TEXT,
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: true,
        bg_color_key: "status",
      },
      {
        label: "Reference Code",
        key: "pickup_code",
        type: ADT.TEXT,
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: false,
        bg_color_key: "status",
        render: (_) => {
          return (
            <div className="flex justify-center">
              <a target="_blank" className="font-inter text-primary underline font-semibold" href={`/pickup/tracking?awb=${_}`}>
                {_}
              </a>
              <button
                type="button"
                onClick={async () => {
                  await navigator.clipboard.writeText(_ || "-");
                  UseToast("Copied!");
                }}
              >
                <DocumentDuplicateIcon className="h-5 -mb-1 ml-2" />
              </button>

              <button
                type="button"
                onClick={async () => {
                  setIsPopupOpen(_);
                }}
              >
                <EnvelopeOpenIcon className="h-5 -mb-1 ml-2" />
              </button>
            </div>
          );
        },
      },
      {
        label: "Date",
        key: "pickup_date_time",
        type: ADT.DATETIME,
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: true,
        dateFormat: "DD-MM-YYYY HH:mm:ss",
        bg_color_key: "status",
        css: "min-w-[100px]",
      },
      {
        label: "Reg. At",
        key: "createdAt",
        type: ADT.DATETIME,
        view: true,
        dateFormat: "DD-MM-YYYY HH:mm:ss",
        add: false,
        edit: false,
        filter: false,
        sort: false,
        bg_color_key: "status",
        css: "min-w-[100px]",
      },
      {
        label: "Project Code",
        key: "project_code",
        type: ADT.TEXT,
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: false,
        bg_color_key: "status",
      },
      {
        label: "Shipper",
        key: "shipper_sh",
        type: ADT.TEXT,
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: true,
        bg_color_key: "status",
      },
      {
        label: "Consignee",
        key: "consignee_cgnee",
        type: ADT.TEXT,
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: true,
        bg_color_key: "status",
      },
      {
        label: "Origin",
        key: "shipper_city",
        type: ADT.TEXT,
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: true,
        bg_color_key: "status",
      },
      {
        label: "Dest.",
        key: "consignee_city",
        type: ADT.TEXT,
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: true,
        bg_color_key: "status",
      },
      {
        label: "Pcs",
        key: "quantity",
        type: ADT.TEXT,
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: false,
        bg_color_key: "status",
      },
      {
        label: "WT",
        key: "weight",
        type: ADT.TEXT,
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: true,
        bg_color_key: "status",
      },
      {
        label: "Booked AWB",
        key: "foundAwbs",
        type: ADT.TEXT,
        view: true,
        bg_color_key: "status",
      },
      {
        label: "Emp Name",
        key: "assgined_to_agent_name",
        type: ADT.DB,
        db: "user",
        db_col: "name",
        bg_color_key: "status",
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: false,
      },

      {
        label: "Status",
        key: "status",
        type: ADT.SELECT,
        bg_color_key: "status",
        bg_color: {
          0: "",
          1: "green-200",
          2: "red-200",
          3: "yellow-200",
        },
        options: [
          { value: 0, label: "Pickup Processed" },
          { value: 1, label: "Pickup Done" },
          { value: 2, label: "Pickup Cancel" },
          { value: 3, label: "Pickup Reschedule" },
          { value: 4, label: "Confirmed" },
        ],
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: true,
      },
      {
        label: "Remark",
        key: "status_reason",
        type: ADT.TEXT,
        view: true,
        bg_color_key: "status",
      },
      {
        label: "Last Update",
        key: "updatedBy",
        type: ADT.DB,
        db: "user",
        db_col: "name",
        bg_color_key: "status",
        view: true,
        add: true,
        edit: true,
        filter: false,
        sort: false,
      },
    ],
    [],
  );
  const [isPopupOpen, setIsPopupOpen] = useState<any>("");

  useEffect(() => {
    const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
    if (Object.keys(userD).length) {
      setUserDetails(userD.state.user);
      console.log("userD.state", userD.state.user);
      if (userD.state.user.roleName !== roles.SUPER_ADMIN && userD.state.user.roleName !== roles.BRANCH_ADMIN) {
        setBranchOtp(userD.state.user.branch_name);
        setSubBranchOtp(userD.state.user.branch_name);
      }
      mutateListAgents();
    }
  }, []);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const typeOptions: any = [
    { value: "DOX", label: "DOX" },
    { value: "NON-DOX", label: "NON-DOX" },
  ];
  const ten_30Options: any = [
    { value: 1, label: "Yes" },
    { value: 0, label: "No" },
  ];
  const holidayOptions: any = [
    { value: 1, label: "Yes" },
    { value: 0, label: "No" },
  ];

  const boxSizeOptions: any[] = [
    { value: "pkg1Charge", label: "1 LT", field: "pkg1Charge" },
    { value: "pkg2Charge", label: "4 LT", field: "pkg2Charge" },
    { value: "pkg3Charge", label: "10 LT", field: "pkg3Charge" },
    { value: "cube6", label: "CUBE-6" },
    { value: "cube8", label: "CUBE-8" },
    { value: "cube13", label: "CUBE-13" },
    { value: "cube25", label: "CUBE-25" },
    { value: "cube56", label: "CUBE-56" },
  ];

  const tempRangeOptions: any = [
    { value: "-80", label: "-80" },
    { value: "-20", label: "-20" },
    { value: "2 to 8", label: "2 to 8" },
    { value: "15 to 25", label: "15 to 25" },
  ];

  const statusOptions: any = [
    { value: 0, label: "0 - Pickup Processed" },
    { value: 1, label: "1 - Pickup Done" },
    { value: 2, label: "2 - Pickup Cancel" },
    { value: 3, label: "3 - Pickup Reschedule" },
    { value: 4, label: "4 - Confirmed" },
  ];

  const [productOptions, setProductOptions] = useState<any>([{ value: 0, label: "Loading..." }]);

  const { error, isFetching, isError, isFetched, isLoading, refetch } = useQuery(
    ["ProductsListApi"],
    () => ListCommanWay("/admin/product/list", {}, { paginate: 1000 }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt:any = [];
          data.data.data.forEach((p) => {
            opt.push({
              value: p.id,
              label: p.product_name,
            });
          });
          setProductOptions(opt);
        }
      },
    },
  );

  const [subbranchOptions, setSubBranchOptions] = useState<any>([{ value: 0, label: "Loading..." }]);

  const {
    error: subbranchError,
    isFetching: subbranchIsFetching,
    isError: subbranchIsError,
    isFetched: subbranchIsFetched,
    isLoading: subbranchIsLoading,
    refetch: subbranchRefetch,
  } = useQuery(["SubBranchesListApi"], () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        let opt: any[] = [];
        data.data.data.forEach((p) => {
          opt.push({
            value: p.id,
            label: p.branch_name,
          });
        });
        setSubBranchOptions(opt);
      }
    },
  });

  const [branchOptions, setBranchOptions] = useState<any>([{ value: 0, label: "Loading..." }]);

  const {
    error: branchError,
    isFetching: branchIsFetching,
    isError: branchIsError,
    isFetched: branchIsFetched,
    isLoading: branchIsLoading,
    refetch: branchRefetch,
  } = useQuery(["BranchesListApi"], () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        let opt: any[] = [];
        data.data.data.forEach((p) => {
          opt.push({
            value: p.id,
            label: p.branch_name,
          });
        });
        setBranchOptions(opt);
      }
    },
  });
  const [agentOptions, setAgentOptions] = useState<any>([{ value: 0, label: "Loading..." }]);

  useEffect(() => {
    if (isBranchSet) {
      mutateListAgents(values.branch_name);
    }
  }, [isBranchSet]);

  const { mutate: mutateListAgents, isLoading: isAgentsloading }: any = useMutation(
    (qry?: any) =>
      ListCommanWay(
        "/admin/user/list",
        {
          isActive: true,
          subbranch: isBranchSet,
        },
        { paginate: 1000 },
      ),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          let opt: any[] = [];
          data.data.data.forEach((p) => {
            opt.push({
              value: p.id,
              label: p.name ? `${p.name} (${p.empCode || p.username})` : p.username,
            });
          });
          setAgentOptions(opt);
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    },
  );

  const { handleSubmit, handleBlur, values, handleChange, errors, setValues, setErrors, touched, dirty, setFieldValue, resetForm } =
    useFormik({
      validationSchema: object().shape({}),
      initialValues: {
        date: moment().format("YYYY-MM-DD"),
        pickup_date_time: moment().format("YYYY-MM-DD HH:mm"),
        doSendWapp: true,
        quantity: "",
        type: { value: 0 },
        customer_code: "",
        weight: 0,
        ten_30: { value: 0 },
        project_code: "",
        product_name: { value: 0, label: "" },
        holiday: { value: 0, label: "" },

        customer_pincode: "",
        customer_mobile: "",
        customer_sh: "",
        customer_city: "",
        customer_state: "",
        customer_country: "",
        customer_address: "",
        change_consignee: false,
        consignee_pincode: "",
        consignee_mobile: "",
        consignee_cgnee: "",
        consignee_city: "",
        consignee_state: "",
        consignee_country: "",
        consignee_address: "",
        consignee_company: "",

        shipper_pincode: "",
        shipper_mobile: "",
        shipper_sh: "",
        shipper_city: "",
        shipper_state: "",
        shipper_country: "",
        shipper_pan: "",
        shipper_gst_no: "",
        shipper_address: "",
        shipper_company: "",

        dice_weight: 0,
        oda_charge: 0,
        dg_charge: 0,
        pkg_charge: 0,
        pkg1Charge: 0,
        pkg2Charge: 0,
        pkg3Charge: 0,
        pkgData: "",
        selectedBoxSize: "",
        boxModel: "",
        tempData: "",
        d_ice_charge: 0,

        branch_id: { value: 0, label: "" },
        subbranch: { value: 0, label: "" },
        branch_name: { value: 0, label: "" },
        assgined_to_agent_name: { value: 0, label: "" },
      },
      onSubmit: async (values, { resetForm }) => {
        let req: any = { ...values };
        req.pickup_date_time = pickupDate + "T" + pickupTime;
        req.type = req.type ? req.type.value : undefined;
        req.product_name = req.product_name ? req.product_name.label : undefined;
        req.assgined_to_branch_name = req.branch_name ? req.branch_name.value : null;
        userDetails?.roleName !== roles.SUPER_ADMIN && userDetails?.roleName !== roles.BRANCH_ADMIN
          ? (req.subbranch = userDetails.subbranch)
          : (req.subbranch = req.subbranch ? req.subbranch.value : null);

        // req.subbranch = req.subbranch ? req.subbranch.value : null;
        req.assgined_to_agent_name = req.assgined_to_agent_name ? req.assgined_to_agent_name.value : null;
        if (!req.assgined_to_agent_name) {
          delete req.assgined_to_agent_name;
        }
        if (req.assgined_to_branch_name == 0) {
          delete req.assgined_to_branch_name;
        }

        if (req.status == undefined || req.status.value == -1) {
          delete req.status;
        }

        if (typeof req.holiday == "object") {
          req.holiday = req.holiday.value === 1 ? true : false;
        } else {
          req.holiday = req.holiday === 1 ? true : false;
        }

        if (typeof req.ten_30 == "object") {
          req.ten_30 = req.ten_30.value === 1 ? true : false;
        } else {
          req.ten_30 = req.ten_30 === 1 ? true : false;
        }

        req.weight = parseFloat(req.weight);
        req.customer_mobile = req.customer_mobile.toString();

        req.oda_charge = parseFloat(req.oda_charge);
        req.pkg_charge = parseFloat(req.pkg_charge);
        req.d_ice_charge = parseFloat(req.d_ice_charge);

        if (!req.assgined_by_third_party) {
          delete req.assgined_by_third_party;
        }
        if (!req.assgined_by_branch) {
          delete req.assgined_by_branch;
        }
        if (!req.shipper_sms_disable) {
          delete req.shipper_sms_disable;
        }
        let toDeleteBlank = ["dice_weight", "d_ice_charge", "dg_charge", "pkg_charge", "pkg1Charge", "pkg2Charge", "pkg3Charge"];
        toDeleteBlank.forEach((x) => {
          if (!req[x]) {
            req[x] = 0;
          }
        });

        // Handle box size selection
        const selectedBox = getPkgBoxSizeValue(req.selectedBoxSize);
        const boxModel = req.boxModel?.trim() || "";
        if (selectedBox) {
          const knownFields = ["pkg1Charge", "pkg2Charge", "pkg3Charge"];
          if (knownFields.includes(selectedBox)) {
            req[selectedBox] = 1;
            if (boxModel) req.pkgData = { boxModel };
          } else {
            req.pkgData = { boxSize: selectedBox, ...(boxModel && { boxModel }) };
          }
        } else if (boxModel) {
          req.pkgData = { boxModel };
        }
        delete req.selectedBoxSize;
        delete req.boxModel;
        if (!req.pkgData) delete req.pkgData;

        // Handle temp range
        if (req.tempData && typeof req.tempData === "object") {
          req.tempData = req.tempData.value;
        }
        if (!req.tempData) delete req.tempData;

        // Convert "0" customer code to blank
        if (req.customer_code === "0") {
          req.customer_code = "";
        }

        if (req.customer_code == "") {
          if (!window.confirm("Customer Code is Blank, Click ok to continue, Click Cancel to Edit Customer?")) {
            return;
          }
        }

        // Block if customer code is entered but not validated
        if (req.customer_code !== "" && custCodeValid === false) {
          return UseToast("Invalid Customer Code. Please enter a valid code.", "error");
        }
        if (req.customer_code !== "" && custCodeValid === null) {
          return UseToast("Please wait for Customer Code validation to complete.", "error");
        }
        if (req.shipper_pincode == "" || req.shipper_city == "") {
          return alert("Shipper Pincode / City is missing");
        }
        mutate({
          url: "/admin/pickup" + (editMode ? "/update/" + selectedData.id : "/create"),
          data: req,
        });
      },
    });

  const changeStatus = () => {
    mutate({
      url: "/admin/pickup/status/" + selectedData.id,
      data: {
        status: selectedStatus.value,
        status_reason: statusReason,
        pickup_date_time: rescheduleDate != "" ? rescheduleDate : undefined,
      },
    });
  };

  const {
    mutate,
    isLoading: isloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation(editMode ? UpdateCommanWay : CreateCommanWay, {
    onSuccess: (data: any) => {
      if (data.status == "SUCCESS") {
        //remove all window reload
        //window.location.reload();
        //return;
        if (!editMode) {
          UseToast("Data Added Successfully", "success");
          setIsDataAdded(!isDataAdded);
          setPickupCode(data.data.pickup_code);
          setEmployeeWapp(data.data?.whatsapp_copy);
          openModal();
          setEditMode(false);
          resetForm();
          setSelectedStatus("");
          setStatusReason("");
          setRescheduleDate("");
        } else {
          UseToast("Data Updated Successfully", "success");
          setIsDataAdded(!isDataAdded);
          setEditMode(false);
          resetForm();
          setSelectedStatus("");
          setStatusReason("");
          setRescheduleDate("");
        }
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: manualEmailMutate,
    isLoading: isManualEmailLoading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation(CreateCommanWay, {
    onSuccess: (data: any) => {
      if (data.status == "SUCCESS") {
        console.log("data__", data.data);
        //remove all window reload
        //window.location.reload();
        //return;
        UseToast("Data Added Successfully", "success");
        setIsDataAdded(!isDataAdded);
        setPickupCode(data.data.pickupCode);
        setEmployeeWapp(data.data?.whatsapp_copy);
        setAddedPickupDate(data.data?.date);
        openModal();
        setEditMode(false);
        resetForm();
        setSelectedStatus("");
        setStatusReason("");
        setRescheduleDate("");
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: mutatePincode,
    isLoading: isPincodeloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((pincode) => GetCommanWay("/admin/address/" + pincode, {}, {}), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        Object.keys(data.data).map((d) => {
          values[pincodeSection + "_" + d] = data.data[d];
        });
        return data.data;
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      values[pincodeSection + "_city"] = "";
      values[pincodeSection + "_country"] = "";
      values[pincodeSection + "_state"] = "";
      return { city: "", country: "", pincode: "", region: "", state: "" };
    },
  });
  const { mutate: mutateSavedMobile }: any = useMutation((code) => ListCommanWay("/admin/savedcustomer/list/", { mobile: code }, {}), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        if (data.data?.data?.length > 0) {
          console.log(mobileSection.split("|")[1]);
          if (mobileSection.split("|")[1] == "customer_mobile") {
            setFieldValue("customer_sh", data.data.data[0].name);
            setFieldValue("customer_address", data.data.data[0].address);
          } else if (mobileSection.split("|")[1] == "consignee_mobile") {
            setFieldValue("consignee_cgnee", data.data.data[0].name);
            setFieldValue("consignee_address", data.data.data[0].address);
          } else if (mobileSection.split("|")[1] == "shipper_mobile") {
            setFieldValue("shipper_sh", data.data.data[0].name);
            setFieldValue("shipper_address", data.data.data[0].address);
          }
        }

        return data.data;
      }
    },
    onError: (data: any) => {
      //typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });
  useEffect(() => {
    if (mobileSection) {
      mutateSavedMobile(mobileSection.split("|")[0]);
    }
  }, [mobileSection]);

  const { mutate: mutateCustomer }: any = useMutation((code) => GetCommanWay("/admin/customer/get/" + code, {}, {}), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS" && data.data) {
        setCustCodeValid(true);
        Object.keys(values).map((d) => {
          if (d.split("customer_")[1]) {
            console.log(d.split("customer_")[1], values["customer_" + d.split("customer_")[1]], data.data[d.split("customer_")[1]]);
            if (d != "customer_mobile") {
              values["customer_" + d.split("customer_")[1]] = data.data[d.split("customer_")[1]];
            }
          }
        });
        return data.data;
      } else {
        setCustCodeValid(false);
        UseToast("Invalid Customer Code - customer not found", "error");
      }
    },
    onError: (data: any) => {
      setCustCodeValid(false);
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const handleEdit = useCallback(
    (_data) => {
      let data = { ..._data };
      console.log("data", data);

      console.log(data.pickup_date_time.substring(0, 4).includes("-"), data.pickup_date_time);
      if (data.pickup_date_time.substring(0, 4).includes("-")) {
        //data.pickup_date_time = moment(data.pickup_date_time, "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD HH:mm");
        setPickupDate(moment(data.pickup_date_time, "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD"));
        setPickupTime(moment(data.pickup_date_time, "DD-MM-YYYY HH:mm:ss").format("HH:mm"));
      } else {
        //data.pickup_date_time = moment(data.pickup_date_time, "YYYY-MM-DD HH:mm").format("YYYY-MM-DD HH:mm");
        setPickupDate(moment(data.pickup_date_time, "YYYY-MM-DD HH:mm").format("YYYY-MM-DD"));
        setPickupTime(moment(data.pickup_date_time, "YYYY-MM-DD HH:mm").format("HH:mm"));
      }

      setEmployeeWapp(data?.whatsapp_copy);

      setSelectedData(data);
      setEditMode(true);
      // Trust existing customer code from DB
      setCustCodeValid(data.customer_code ? true : null);

      // Restore tempData as react-select object
      if (data.tempData) {
        const found = tempRangeOptions.find((o: any) => o.value === data.tempData);
        if (found) setFieldValue("tempData", found);
      }

      // Restore selectedBoxSize from dedicated fields or pkgData
      const selectedBoxSize = getPkgBoxSizeValue(data.pkgData);

      if (data.pkg1Charge) {
        setFieldValue("selectedBoxSize", "pkg1Charge");
      } else if (data.pkg2Charge) {
        setFieldValue("selectedBoxSize", "pkg2Charge");
      } else if (data.pkg3Charge) {
        setFieldValue("selectedBoxSize", "pkg3Charge");
      } else if (selectedBoxSize) {
        setFieldValue("selectedBoxSize", selectedBoxSize);
      }

      // Restore boxModel from pkgData
      if (data.pkgData) {
        const pkg = typeof data.pkgData === "string" ? (() => { try { return JSON.parse(data.pkgData); } catch { return null; } })() : data.pkgData;
        if (pkg?.boxModel) setFieldValue("boxModel", pkg.boxModel);
      }

      Object.keys(data).map((d) => {
        if (["tempData", "pkgData"].includes(d)) return; // already handled above
        if (Object.keys(values).includes(d)) {
          if (["holiday", "product_name", "ten_30", "type", "subbranch"].includes(d)) {
            /*console.log("{value: data[d],label: data[d] === 0 ?  : data[d] === 1 ? [d],", d, {
            value: data[d] === 0 || data[d] === false ? 0 : data[d] === 1 || data[d] === true ? 1 : data[d],
            label: data[d] === 0 || data[d] === false ? "No" : data[d] === 1 || data[d] === true ? "Yes" : data[d],
          });*/

            setFieldValue(d, {
              value: data[d] === 0 || data[d] === false ? 0 : data[d] === 1 || data[d] === true ? 1 : data[d],
              label: data[d] === 0 || data[d] === false ? "No" : data[d] === 1 || data[d] === true ? "Yes" : data[d],
            });
            if (d === "subbranch" && (userDetails?.roleName === roles.SUPER_ADMIN || userDetails?.roleName === roles.BRANCH_ADMIN)) {
              setFieldValue(d, {
                value: data[d] === 0 || data[d] === false ? 0 : data[d] === 1 || data[d] === true ? 1 : data[d],
                label: data[d] === 0 || data[d] === false ? "No" : data[d] === 1 || data[d] === true ? "Yes" : data[d],
              });
            }
          } else if (d === "assgined_to_agent_name") {
            const agentLabel = data._assgined_to_agent_name
              ? (data._assgined_to_agent_name.name ? `${data._assgined_to_agent_name.name} (${data._assgined_to_agent_name.empCode || data._assgined_to_agent_name.username || ""})` : (data._assgined_to_agent_name.username || ""))
              : "";
            setFieldValue("assgined_to_agent_name", data[d] ? { value: data[d], label: agentLabel } : { value: 0, label: "" });
          } else {
            if (d == "dice_weight") {
              console.log("dice_weight", data["dice_weight"]);
            }

            setFieldValue(d, data[d]);
          }
        }
      });
    },
    [userDetails, setFieldValue, setPickupDate, setPickupTime, setEmployeeWapp, setSelectedData, setEditMode],
  );

  const {
    mutate: mutatePickup,
    isLoading: isPickuploading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((pickup_code) => ListCommanWay("/admin/pickup/list", { pickup_code }, {}), {
    onSuccess: async (data: any) => {
      if (data.status === "SUCCESS") {
        if (data.data?.data?.length > 0) {
          let pdata = data.data.data[0];

          /*typeOptions.forEach((x) => {
            x.value == pdata.type && setFieldValue("type", x);
          });*/
          ten_30Options.forEach((x) => {
            x.value == pdata.ten_30 && setFieldValue("ten_30", x);
          });
          holidayOptions.forEach((x) => {
            x.value == pdata.holiday && setFieldValue("holiday", x);
          });

          /*productOptions.forEach((x) => {
            if (x.label == pdata.product_name) {
              console.log(x.label, pdata.product_name, x);
              setFieldValue("product_name", x);
            }
          });*/

          setFieldValue("date", moment().format("YYYY-MM-DD"));
          setFieldValue("pickup_date_time", moment().format("YYYY-MM-DD"));

          setPickupDate(moment().format("YYYY-MM-DD"));
          setPickupTime(moment().format("HH:mm"));

          // Trust customer code from loaded pickup (already validated when created)
          setCustCodeValid(pdata.customer_code ? true : null);

          Object.keys(pdata).map((obj) => {
            if (
              ![
                "type",
                "createdAt",
                "product_name",
                "ten_30",
                "holiday",
                "type",
                "branch_id",
                "subbranch",
                "branch_name",
                "assgined_to_agent_name",
                "quantity",
                "project_code",
                "weight",
                "status",
                "status_reason",
                "whatsapp_copy",
              ].includes(obj)
            ) {
              setFieldValue(obj, pdata[obj]);
              //values[obj] = pdata[obj];
            }
          });
        }
      }
    },
    onError: (data: any) => {
      //typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const [tableData, setTableData] = useState<string[][]>([]);

  // Handle paste event
  const handlePaste = (event) => {
    event.preventDefault();
    // Get HTML content from clipboard
    const htmlContent = event.clipboardData.getData("text/html");

    // Create a DOM parser to parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const table = doc.querySelector("table");

    if (table) {
      // Extract table data
      const rows = Array.from(table.querySelectorAll("tr")).map((row) =>
        Array.from(row.querySelectorAll("td, th")).map((cell) => cell.textContent.trim()),
      );
      setTableData(rows);
    } else {
      // Fallback for plain text or no table
      const plainText = event.clipboardData.getData("text/plain");
      // Convert plain text to a simple table (basic parsing)
      const rows = plainText.split("\n").map((row) => row.split("\t").map((cell) => cell.trim()));
      setTableData(rows.filter((row) => row.some((cell) => cell))); // Remove empty rows
    }
  };
  const handleCellChange = (rowIndex, colIndex, value) => {
    const updatedData = [...tableData];
    updatedData[rowIndex][colIndex] = value;
    setTableData(updatedData);
  };

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter flex-1">
      {isloading && <Spinner />}

      {/* Manual Email Popup */}
      {manualEmailPopup && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="rounded-2xl shadow-xl w-full max-w-4xl max-h-[95vh] overflow-y-auto relative bg-white p-4">
            {isManualEmailLoading && <Spinner />}
            <button className="absolute top-3 right-3 text-slate-400 hover:text-slate-700" onClick={() => setManualEmailPopup(false)}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-lg font-bold text-slate-800 mb-2">Paste Table from Outlook</h2>
            <div className="flex items-center gap-2">
              <textarea
                placeholder="Paste your table here (Ctrl+V)"
                onPaste={handlePaste}
                className="w-full h-12 border rounded-xl p-2 text-sm"
              />
              <Button
                onPress={() => {
                  const tableContent = `<table border="1" class="border-collapse border"><tbody>${tableData.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
                  manualEmailMutate({ url: "/admin/pickup/manual-mail", data: { htmlBody: tableContent } });
                }}
                color="primary"
                variant="solid"
                size="sm"
              >
                Save Data
              </Button>
            </div>
            {tableData.length > 0 && (
              <table border={1} className="border-collapse w-full border mt-2 text-sm">
                <tbody>
                  {tableData.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, colIndex) => (
                        <td key={`${rowIndex}-${colIndex}`} className="border">
                          <input
                            type="text"
                            value={cell}
                            onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                            className="w-full p-0.5 text-sm"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Box Size Selection Popup */}
      {boxSizePopup && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setBoxSizePopup(false)}>
          <div className="rounded-2xl shadow-xl w-full max-w-lg bg-white p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-800">Select Box Size</h2>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setBoxSizePopup(false)}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex gap-4">
              {/* Left: Box Size Options */}
              <div className="flex-1">
                <p className="text-xs font-semibold text-slate-500 mb-1.5">Box Size</p>
                <div className="grid grid-cols-2 gap-2">
                  {boxSizeOptions.map((opt: any) => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-all text-sm font-medium ${
                        values.selectedBoxSize === opt.value
                          ? "border-primary2 bg-primary2/10 text-primary2"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="selectedBoxSize"
                        value={opt.value}
                        checked={values.selectedBoxSize === opt.value}
                        onChange={() => {
                          // Clear old dedicated fields
                          setFieldValue("pkg1Charge", 0);
                          setFieldValue("pkg2Charge", 0);
                          setFieldValue("pkg3Charge", 0);
                          setFieldValue("selectedBoxSize", opt.value);
                        }}
                        className="w-3.5 h-3.5 text-primary2 focus:ring-primary2/20"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
              {/* Right: Box Model Name */}
              <div className="w-44 flex-shrink-0">
                <p className="text-xs font-semibold text-slate-500 mb-1.5">Box Model</p>
                <input
                  type="text"
                  name="boxModel"
                  list="boxModelSuggestions"
                  value={values.boxModel || ""}
                  onChange={(e) => setFieldValue("boxModel", e.target.value)}
                  placeholder="Type or select model"
                  className="w-full px-2.5 py-1.5 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:border-primary2 focus:ring-2 focus:ring-primary2/20 text-slate-700 placeholder:text-slate-400 mb-2"
                />
                <datalist id="boxModelSuggestions">
                  <option value="CUBE-06F" />
                  <option value="CUBE-06C" />
                  <option value="TEGO 09" />
                </datalist>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {["CUBE-06F", "CUBE-06C", "TEGO 09"].map((model) => (
                    <button
                      key={model}
                      type="button"
                      onClick={() => setFieldValue("boxModel", model)}
                      className={`w-full text-left px-2.5 py-1.5 rounded-lg border text-sm transition-all ${
                        values.boxModel === model
                          ? "border-primary2 bg-primary2/10 text-primary2 font-medium"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {model}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button
                size="sm"
                color="danger"
                variant="flat"
                className="text-xs"
                onPress={() => {
                  setFieldValue("pkg1Charge", 0);
                  setFieldValue("pkg2Charge", 0);
                  setFieldValue("pkg3Charge", 0);
                  setFieldValue("selectedBoxSize", "");
                  setFieldValue("boxModel", "");
                  setBoxSizePopup(false);
                }}
              >
                Clear
              </Button>
              <Button size="sm" color="primary" variant="solid" className="text-xs" onPress={() => setBoxSizePopup(false)}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* View Pickup Mail Popup */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-50 z-[99] flex items-center justify-end p-4">
          <div className="rounded-2xl shadow-xl w-full max-w-5xl max-h-[95vh] overflow-y-auto relative bg-white">
            <button
              className="absolute top-3 right-3 text-slate-400 bg-white rounded-md hover:text-slate-700 z-10"
              onClick={() => setIsPopupOpen("")}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="pt-2">
              <ViewPickupMail pickup_code={isPopupOpen || ""} />
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          window.location.reload();
        }}
      >
        <h2 className="text-2xl font-bold mb-4">New pickup is added</h2>
        <p>Code: {pickupCode}</p>
        <div className="flex gap-4 max-w-7xl">
          <div className="bg-slate-100 p-3 flex-1 rounded-xl">
            Dear all,
            <br />
            <br />
            Your mentioned pickup has been registered for {addedPickupDate} under REFERENCE NUMBER: {pickupCode}.<br />
            <Button
              className="mt-2"
              color="primary"
              size="sm"
              variant="solid"
              onPress={async () => {
                await navigator.clipboard.writeText(
                  pickupCode
                    ? `Dear all,\n\nYour mentioned pickup has been registered for today ${addedPickupDate} under REFERENCE NUMBER : ${pickupCode}.`
                    : "-",
                );
                UseToast("Copied!");
              }}
            >
              Copy
            </Button>
          </div>
          <div className="bg-slate-100 p-3 flex-1 rounded-xl">
            <textarea className="w-full h-32 rounded-lg border p-2 text-sm" value={employeeWapp.replace(/%0A/g, "\n")} readOnly />
            <Button
              className="mt-2"
              color="primary"
              size="sm"
              variant="solid"
              onPress={async () => {
                await navigator.clipboard.writeText(employeeWapp.replace(/%0A/g, "\n") || "-");
                UseToast("Copied!");
              }}
            >
              Copy
            </Button>
          </div>
        </div>
      </Modal>

      {/* ═══════════ HEADER BAR ═══════════ */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">
              Topay Pickup
            </h1>
            <div className="flex items-center gap-2">
              {editMode && (
                <Button color="warning" size="sm" variant="flat" className="h-7 text-xs" onPress={() => window.location.reload()}>
                  Cancel Edit
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ MAIN FORM ═══════════ */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
          {/* ═══════════ LEFT COLUMN ═══════════ */}
          <div className="space-y-2">
            {/* Pickup Registration Card */}
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
              <div className="pb-0 pt-2 px-3">
                <div className="flex items-center justify-between gap-2 w-full">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                    </div>
                    <h2 className="text-sm font-bold text-slate-800">Pickup Reg.</h2>
                    <span className="text-[10px] text-slate-400 mx-1">|</span>
                    <span className="text-[11px] font-medium text-slate-500">Load Ref:</span>
                    <input
                      type="text"
                      className="px-2 py-[2px] rounded-lg border border-slate-200 bg-white text-[12px] outline-none focus:border-primary2 w-32"
                      value={old_pickup_code}
                      onBlur={(e) => {
                        if (e.target.value) mutatePickup(e.target.value || "");
                      }}
                      onChange={(e) => setold_pickup_code(e.target.value)}
                      placeholder="Ref. Code"
                    />

                    <Button color="secondary" size="sm" variant="flat" className="h-7 text-xs" onPress={() => setManualEmailPopup(true)}>
                      <EnvelopeOpenIcon className="w-3.5 h-3.5 mr-1" />
                      Add Via Email
                    </Button>
                  </div>
                </div>
              </div>
              <hr className="mt-1.5 border-slate-200" />
              <div className="px-3 py-2 space-y-1">
                <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                  {Object.keys(userDetails).length &&
                  (userDetails?.roleName === roles.SUPER_ADMIN || userDetails?.roleName === roles.BRANCH_ADMIN) ? (
                    <MSelect
                      label="Branch"
                      name="subbranch"
                      options={subbranchOptions}
                      value={values.subbranch}
                      onChange={(e: any) => setFieldValue("subbranch", e)}
                      onBlur={handleBlur}
                    />
                  ) : (
                    <div />
                  )}
                  <div className="flex items-center gap-1.5">
                    <label className="text-[11px] font-medium text-slate-500 whitespace-nowrap min-w-[52px] text-right">Date</label>
                    <input
                      type="date"
                      className="flex-1 min-w-0 px-2 py-[3px] rounded-lg border border-red-300 bg-red-50 text-[13px] outline-none focus:border-primary2 focus:ring-2 focus:ring-primary2/20 text-slate-700"
                      name="pickup_date_only"
                      min={editMode ? undefined : moment().format("YYYY-MM-DD")}
                      value={pickupDate}
                      onChange={(e) => setPickupDate(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <label className="text-[11px] font-medium text-slate-500 whitespace-nowrap min-w-[52px] text-right">Time</label>
                    <input
                      type="time"
                      className="flex-1 min-w-0 px-2 py-[3px] rounded-lg border border-red-300 bg-red-50 text-[13px] outline-none focus:border-primary2 focus:ring-2 focus:ring-primary2/20 text-slate-700"
                      name="pickup_time_only"
                      value={pickupTime}
                      onChange={(e) => setPickupTime(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                  <MInput
                    label="Cust Code"
                    name="customer_code"
                    value={values.customer_code}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onBlurExtra={(e: any) => {
                      const val = e.target.value?.trim();
                      if (val && val !== "0") {
                        setCustCodeValid(null);
                        mutateCustomer(val);
                      } else {
                        setCustCodeValid(null);
                      }
                    }}
                  />
                  <MInput label="Qty" name="quantity" type="number" value={values.quantity} onChange={handleChange} onBlur={handleBlur} />
                  <MSelect
                    label="Type"
                    name="type"
                    options={typeOptions}
                    value={values.type}
                    onChange={(e: any) => setFieldValue("type", e)}
                    onBlur={handleBlur}
                  />
                </div>

                <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                  <MInput label="Weight" name="weight" type="number" value={values.weight} onChange={handleChange} onBlur={handleBlur} />
                  <MSelect
                    label="10:30"
                    name="ten_30"
                    options={ten_30Options}
                    value={values.ten_30}
                    onChange={(e: any) => setFieldValue("ten_30", e)}
                    onBlur={handleBlur}
                  />
                  <MInput
                    label="Project Code"
                    name="project_code"
                    value={values.project_code}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>

                <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                  <MSelect
                    label="Product"
                    name="product_name"
                    options={productOptions}
                    value={values.product_name}
                    onChange={(e: any) => setFieldValue("product_name", e)}
                    onBlur={handleBlur}
                  />
                  <MInput
                    label="Dry Ice WT"
                    name="dice_weight"
                    type="number"
                    value={values.dice_weight}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <MInput label="ODA Charge" name="oda_charge" value={values.oda_charge} onChange={handleChange} onBlur={handleBlur} />
                </div>

                <div className="hidden">
                  <MSelect
                    label="Holiday"
                    name="holiday"
                    options={holidayOptions}
                    value={values.holiday}
                    onChange={(e: any) => setFieldValue("holiday", e)}
                    onBlur={handleBlur}
                  />
                </div>

                <div className="flex gap-1.5 flex-wrap items-center">
                  <MCheckbox
                    label="DG"
                    name="dg_charge"
                    value={values.dg_charge}
                    onChange={(e: any) => setFieldValue("dg_charge", e.target.checked ? 1 : "")}
                  />
                  <MCheckbox
                    label="Pkg"
                    name="pkg_charge"
                    value={values.pkg_charge}
                    onChange={(e: any) => setFieldValue("pkg_charge", e.target.checked ? 1 : "")}
                  />
                  <button
                    type="button"
                    onClick={() => setBoxSizePopup(true)}
                    className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg border border-slate-200 bg-white hover:border-primary2 transition-all cursor-pointer text-[14px] font-medium text-slate-600"
                  >
                    <svg className="w-3.5 h-3.5 text-primary2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                      />
                    </svg>
                    {values.selectedBoxSize || values.boxModel
                      ? [boxSizeOptions.find((o: any) => o.value === values.selectedBoxSize)?.label, values.boxModel].filter(Boolean).join(" / ") || "Select Box"
                      : "Select Box"}
                  </button>
                  <MSelect
                    label="Temp"
                    name="tempData"
                    options={tempRangeOptions}
                    value={values.tempData}
                    onChange={(e: any) => setFieldValue("tempData", e)}
                    onBlur={handleBlur}
                    className="min-w-[160px]"
                  />
                </div>
              </div>
            </div>

            {/* Assign Employee Card */}
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
              <div className="px-3 py-2 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="w-6 h-6 rounded-md bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <span className="text-xs font-bold text-slate-800">Assign</span>
                  <MSelect
                    label="Branch"
                    name="branch_name"
                    options={branchOptions}
                    disabled={isDisabled || [3, 4, 5, 9].includes(userDetails.role_name)}
                    value={values.branch_name}
                    onChange={(e: any) => {
                      setIsBranchSet(e.value);
                      setFieldValue("branch_name", e);
                    }}
                    onBlur={handleBlur}
                    className="flex-1 min-w-[120px]"
                  />
                  <MSelect
                    label="Agent"
                    name="assgined_to_agent_name"
                    options={agentOptions}
                    value={values.assgined_to_agent_name}
                    onChange={(e: any) => setFieldValue("assgined_to_agent_name", e)}
                    onBlur={handleBlur}
                    className="flex-1 min-w-[120px]"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <textarea
                    className="flex-1 text-[11px] rounded-lg border border-slate-200 p-1.5 h-10 resize-none bg-white"
                    value={employeeWapp?.replaceAll("%0A", "\n") || ""}
                    readOnly
                  />
                  <label className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md border border-slate-200 bg-white cursor-pointer">
                    <input
                      name="doSendWapp"
                      type="checkbox"
                      checked={values.doSendWapp}
                      onChange={(e) => setFieldValue("doSendWapp", e.target.checked)}
                      className="w-3 h-3"
                    />
                    WApp
                  </label>
                  <Button
                    size="sm"
                    color="primary"
                    variant="flat"
                    className="h-6 text-[11px] min-w-0 px-2"
                    onPress={async () => {
                      await navigator.clipboard.writeText(employeeWapp?.replaceAll("%0A", "\n") || "");
                      UseToast("Copied!");
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            </div>

            {/* Status Update Card */}
            <div className="shadow-xl shadow-slate-200/50 bg-slate-200/80 backdrop-blur-sm overflow-visible rounded-xl">
              <div className="px-3 py-2 space-y-1.5 text-center">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="w-6 h-6 rounded-md bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <span className="text-xs font-bold text-slate-800">Status Update</span>
                  <MSelect
                    label="Status"
                    name="status_select"
                    options={statusOptions}
                    value={selectedStatus}
                    onChange={(e: any) => setSelectedStatus(e)}
                    className="flex-1 min-w-[140px]"
                  />
                  <MInput
                    label="Remark"
                    name="status_remark"
                    value={statusReason}
                    onChange={(e: any) => setStatusReason(e.target.value)}
                    className="flex-1 min-w-[120px]"
                  />
                </div>
                {selectedStatus.value == 3 && (
                  <MInput
                    label="Reschedule Date"
                    name="reschedule_date"
                    type="date"
                    value={rescheduleDate}
                    onChange={(e: any) => setRescheduleDate(e.target.value)}
                  />
                )}
                <Button size="sm" color="primary" variant="solid" className="font-semibold h-7 text-xs" onPress={changeStatus}>
                  Update Status
                </Button>
              </div>
            </div>
          </div>

          {/* ═══════════ RIGHT COLUMN ═══════════ */}
          <div className="space-y-2">
            {/* Customer Card — vertical title on left */}
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
              <div className="p-0">
                <div className="flex">
                  <div className="w-7 flex-shrink-0 bg-primary/50 rounded-l-xl flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">CUSTOMER</span>
                  </div>
                  <div className="flex-1 px-2.5 py-2 space-y-1">
                    <div className="hidden">
                      <MInput
                        label="Country"
                        name="customer_country"
                        value={values.customer_country}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                      <MInput
                        label="Mobile"
                        name="customer_mobile"
                        value={values.customer_mobile}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onBlurExtra={(e: any) => setMobileSection(e.target.value + "|customer_mobile")}
                      />
                      <MInput
                        label="S/H"
                        name="customer_sh"
                        value={values.customer_sh}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="col-span-2"
                      />
                      <MInput
                        label="Pincode"
                        name="customer_pincode"
                        value={values.customer_pincode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onBlurExtra={(e: any) => {
                          setPincodeSection("customer");
                          mutatePincode(e.target.value);
                        }}
                      />
                      <MInput
                        label="City"
                        name="customer_city"
                        value={values.customer_city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled
                      />
                      <MInput
                        label="State"
                        name="customer_state"
                        value={values.customer_state}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled
                      />
                    </div>
                    <MInput
                      label="Address"
                      name="customer_address"
                      value={values.customer_address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Shipper Card — vertical title on left */}
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
              <div className="p-0">
                <div className="flex">
                  <div className="w-7 flex-shrink-0 bg-primary/50 rounded-l-xl flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">SHIPPER</span>
                  </div>
                  <div className="flex-1 px-2.5 py-2 space-y-1">
                    <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                      <MInput
                        label="Pincode"
                        name="shipper_pincode"
                        value={values.shipper_pincode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onBlurExtra={(e: any) => {
                          setPincodeSection("shipper");
                          mutatePincode(e.target.value);
                        }}
                      />
                      <MInput
                        label="Mobile"
                        name="shipper_mobile"
                        value={values.shipper_mobile}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onBlurExtra={(e: any) => setMobileSection(e.target.value + "|shipper_mobile")}
                      />
                      <MInput label="S/H" name="shipper_sh" value={values.shipper_sh} onChange={handleChange} onBlur={handleBlur} />
                      <MInput
                        label="City"
                        name="shipper_city"
                        value={values.shipper_city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled
                      />
                      <MInput
                        label="State"
                        name="shipper_state"
                        value={values.shipper_state}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled
                      />
                      <MInput
                        label="Country"
                        name="shipper_country"
                        value={values.shipper_country}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled
                      />
                      <MInput label="PAN" name="shipper_pan" value={values.shipper_pan} onChange={handleChange} onBlur={handleBlur} />
                      <MInput label="GST" name="shipper_gst_no" value={values.shipper_gst_no} onChange={handleChange} onBlur={handleBlur} />
                      <MInput
                        label="Company"
                        name="shipper_company"
                        value={values.shipper_company}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                    <MInput
                      label="Address"
                      name="shipper_address"
                      value={values.shipper_address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Consignee Card — vertical title on left with "= Customer" strip */}
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
              <div className="p-0">
                <div className="flex">
                  <div className="w-7 flex-shrink-0 bg-primary/50 rounded-l-xl flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">CONSIGNEE</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFieldValue("consignee_pincode", values.customer_pincode);
                      setFieldValue("consignee_cgnee", values.customer_sh);
                      setFieldValue("consignee_city", values.customer_city);
                      setFieldValue("consignee_state", values.customer_state);
                      setFieldValue("consignee_country", values.customer_country);
                      setFieldValue("consignee_address", values.customer_address);
                    }}
                    className="w-6 flex-shrink-0 bg-gradient-to-b from-secondary/70 to-secondary flex items-center justify-center cursor-pointer hover:from-secondary hover:to-secondary/90 transition-all m-1 rounded-md"
                  >
                    <span className="text-[12px] text-white tracking-wider [writing-mode:vertical-lr] rotate-180 leading-tight font-RobotoCondensed">
                      Same as Customer
                    </span>
                  </button>
                  <div className="flex-1 px-2.5 py-2 space-y-1">
                    <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                      <MInput
                        label="Mobile"
                        name="consignee_mobile"
                        value={values.consignee_mobile}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onBlurExtra={(e: any) => setMobileSection(e.target.value + "|consignee_mobile")}
                      />
                      <MInput
                        label="Company"
                        name="consignee_company"
                        value={values.consignee_company}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <MInput
                        label="Cgnee"
                        name="consignee_cgnee"
                        value={values.consignee_cgnee}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <MInput
                        label="Pincode"
                        name="consignee_pincode"
                        value={values.consignee_pincode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onBlurExtra={(e: any) => {
                          setPincodeSection("consignee");
                          mutatePincode(e.target.value);
                        }}
                      />
                      <MInput
                        label="City"
                        name="consignee_city"
                        value={values.consignee_city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled
                      />
                      <MInput
                        label="State"
                        name="consignee_state"
                        value={values.consignee_state}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled
                      />
                    </div>
                    <div className="hidden">
                      <MInput
                        label="Country"
                        name="consignee_country"
                        value={values.consignee_country}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled
                      />
                    </div>
                    <MInput
                      label="Address"
                      name="consignee_address"
                      value={values.consignee_address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Generate / Update Button */}
            <div className="flex justify-center">
              <Button
                type="submit"
                size="lg"
                className="bg-gradient-to-r from-[#001f3f] to-[#003366] text-white font-bold rounded-xl shadow-lg shadow-[#001f3f]/30 h-10 text-sm tracking-wide hover:shadow-xl hover:shadow-[#001f3f]/40 transition-all"
                isLoading={isloading}
              >
                {editMode ? "✏️ Update Pickup" : "🚀 Generate Pickup"}
              </Button>
            </div>
          </div>
        </div>
      </form>

      {/* ═══════════ FILTERS & DATA TABLE ═══════════ */}
      <div className="mt-3">
        <div className="shadow-xl shadow-slate-200/50 bg-white/80 rounded-xl">
          <div className="px-3 py-2">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 px-2.5 py-1.5 rounded-xl border border-slate-200 bg-white cursor-pointer">
                <input
                  type="checkbox"
                  name="filter_no_booking"
                  checked={filters.filter_no_booking}
                  onChange={(e) => setFilters((prev: any) => ({ ...prev, [e.target.name]: e.target.checked }))}
                  className="w-3.5 h-3.5 rounded"
                />
                Only 0 booking
              </label>

              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-slate-500">Ref:</span>
                <input
                  type="text"
                  className="px-2.5 py-1 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:border-primary2 focus:ring-2 focus:ring-primary2/20 w-32"
                  placeholder="Ref Code"
                  name="filter_pickup_code"
                  value={filters.filter_pickup_code}
                  onChange={(e) => setFilters((prev: any) => ({ ...prev, [e.target.name]: e.target.value }))}
                />
              </div>

              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-slate-500">From:</span>
                <input
                  type="date"
                  className="px-2.5 py-1 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:border-primary2 focus:ring-2 focus:ring-primary2/20"
                  name="filter_date"
                  value={filters.filter_date}
                  onChange={(e) => setFilters((prev: any) => ({ ...prev, [e.target.name]: e.target.value }))}
                />
              </div>

              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-slate-500">To:</span>
                <input
                  type="date"
                  className="px-2.5 py-1 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:border-primary2 focus:ring-2 focus:ring-primary2/20"
                  name="filter_date_to"
                  value={filters.filter_date_to}
                  onChange={(e) => setFilters((prev: any) => ({ ...prev, [e.target.name]: e.target.value }))}
                />
              </div>

              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-slate-500">Cust:</span>
                <input
                  type="text"
                  className="px-2.5 py-1 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:border-primary2 focus:ring-2 focus:ring-primary2/20 w-28"
                  placeholder="Customer Code"
                  name="filter_customer_code"
                  value={filters.filter_customer_code || ""}
                  onChange={(e) => setFilters((prev: any) => ({ ...prev, [e.target.name]: e.target.value }))}
                />
              </div>

              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-slate-500">Origin:</span>
                <input
                  type="text"
                  className="px-2.5 py-1 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:border-primary2 focus:ring-2 focus:ring-primary2/20 w-28"
                  placeholder="Origin City"
                  name="filter_origin"
                  value={filters.filter_origin || ""}
                  onChange={(e) => setFilters((prev: any) => ({ ...prev, [e.target.name]: e.target.value }))}
                />
              </div>

              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-slate-500">Dest:</span>
                <input
                  type="text"
                  className="px-2.5 py-1 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:border-primary2 focus:ring-2 focus:ring-primary2/20 w-28"
                  placeholder="Dest City"
                  name="filter_destination"
                  value={filters.filter_destination || ""}
                  onChange={(e) => setFilters((prev: any) => ({ ...prev, [e.target.name]: e.target.value }))}
                />
              </div>

              <Button
                size="sm"
                color="success"
                variant="solid"
                onPress={() => {
                  setCurrentPage(1);
                  setIsDataAdded(!isDataAdded);
                }}
              >
                Search
              </Button>
            </div>

            <MemoizedPickupDatatable
              apiUrl="/admin/pickup/list"
              columnData={tableColumnData}
              isDataAdded={isDataAdded}
              handleEdit={handleEdit}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              query={datatableQuery}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToPayPickpAdd;
